'use strict';
import { deleteThread, getThreads } from '@api/aiAnalyzer.js';
import Search from '@components/Common/Search.js';
import toast from '@components/Common/Toast.js';
import Modal from '@components/Modals/Modal.js';
import { TrashIcon } from '@icons/index.js';
import { UIButton, UIDiv, UIText } from '@ui';
import { getDateDisplay } from '@utils/dateUtils.js';

class AiAnalyzerHistory extends UIDiv {
  constructor(loadThread) {
    super();
    this.setClass('flex flex-col h-full');
    this.threads = [];
    this.loadThread = loadThread;
    this.searchQuery = '';
    this.#buildComponent();
  }

  async loadHistory() {
    try {
      const response = await getThreads();
      const newThreads = response.body.map(thread => ({
        ...thread,
        messages: thread.messages.map(message => ({
          ...JSON.parse(message),
        })),
      }));
      this.threads = newThreads;
      this.#updateThreadList();
    } catch (error) {
      console.error('Error loading threads:', error);
      toast.error(i18next.t('sidebar.aiAnalyzer.toast.failed_to_load_history'));
    }
  }

  #updateThreadList() {
    const listContainer = this.find('#threads-list')[0];
    if (!listContainer) return;

    listContainer.clear();

    const filteredThreads = this.threads.filter(thread =>
      thread.title.toLowerCase().includes(this.searchQuery.toLowerCase()),
    );

    if (filteredThreads.length === 0) {
      const emptyState = new UIText(
        this.searchQuery
          ? i18next.t('sidebar.aiAnalyzer.no_search_results')
          : i18next.t('sidebar.aiAnalyzer.history_empty'),
      );
      emptyState.setClass('text-gray-500 text-center mt-4');
      listContainer.add(emptyState);
      return;
    }

    filteredThreads.forEach(thread => {
      const threadItem = new UIDiv();
      threadItem.setClass(
        'border-b border-gray-200 py-4 px-2 hover:bg-gray-50 hover:cursor-pointer',
      );

      threadItem.onClick(e => {
        e.stopPropagation();
        this.loadThread(thread);
      });

      const titleWrapper = new UIDiv();
      titleWrapper.setClass('flex justify-between items-center');

      const title = new UIText(thread.title);
      title.setClass('text-lg font-medium mb-2');
      titleWrapper.add(title);
      // Add delete button
      const deleteButton = new UIButton();
      deleteButton.setClass('ml-2 p-1 hover:bg-gray-100 rounded-md');
      const deleteIcon = new TrashIcon({
        size: 16,
        color: 'none',
        strokeColor: 'red',
      });
      deleteButton.add(deleteIcon);
      deleteButton.onClick(async e => {
        e.stopPropagation();
        const modal = new Modal('', {
          description: i18next.t('sidebar.aiAnalyzer.remove_thread_confirm'),
          cancelText: i18next.t('common.cancel'),
          confirmText: i18next.t('common.delete'),
          confirmClass:
            'bg-[var(--o-hive-error-80)] text-white hover:enabled:bg-[var(--o-hive-error-100)]',
          handleOnAction: async () => {
            await deleteThread(thread.id);
            this.threads = this.threads.filter(t => t.id !== thread.id);
            this.#updateThreadList();
          },
        });

        await modal.show();
      });
      titleWrapper.add(deleteButton);
      const metadata = new UIDiv();
      metadata.setClass(
        'flex justify-between items-center mt-2 text-sm text-gray-600',
      );

      const stats = new UIDiv();
      stats.setClass('flex gap-3');

      const createdAt = new UIText(getDateDisplay(thread.createdAt));

      const messagesCount = new UIText(
        `${i18next.t('sidebar.aiAnalyzer.messages_count', {
          count: thread.messages.length,
        })}`,
      );
      //   const filesCount = new UIText(`Files: ${thread.files.length}`);

      stats.add(messagesCount);
      //   stats.add(filesCount);

      threadItem.add(titleWrapper);
      threadItem.add(metadata);
      metadata.add(stats);
      metadata.add(createdAt);

      listContainer.add(threadItem);
    });
  }

  #buildComponent() {
    // Add search container
    const search = new Search({
      handleSearch: searchValue => {
        this.searchQuery = searchValue;
        this.#updateThreadList();
      },
      className: 'my-2 px-1',
      placeholder: i18next.t('common.search'),
    });
    this.add(search);
    this.add(search);

    const listContainer = new UIDiv();
    listContainer.setId('threads-list');
    listContainer.setClass('flex-1 overflow-y-auto');
    this.add(listContainer);
  }
}

export { AiAnalyzerHistory };
